import {
  DateInput,
  Icon,
  IconButton,
  Input,
  Select,
  Stack,
  Title,
  useTheme
} from "@introist/react-foundation/v2";
import { DatasetField } from "../../index";
import { useDataset } from "../../hooks/useDataset";
import { useEmployees } from "modules/employees/hooks/useEmployees";

type DatasetEntryFieldProps = {
  field: DatasetField;
  value: string | null;
  onChange: (val: string | null) => void;
  hasChanges?: boolean;
  error?: string;
  onClear?: () => void;
  autoFocus?: boolean;
};

export const DatasetEntryField = (props: DatasetEntryFieldProps) => {
  const { field, value, onChange, hasChanges, error, onClear, autoFocus } = props;
  const { theme } = useTheme();

  const resolveField = () => {
    const fieldStyles = {
      flex: 1,
      background: hasChanges && !error ? theme.palette.surface.subdued : undefined
    };

    if (field.type === "reference") {
      return <DatasetLinkField {...props} />;
    }
    if (field.type === "employee") {
      return <DatasetEmployeeField {...props} />;
    }
    if (field.type === "date") {
      return (
        <DateInput
          size="small"
          variant="blended"
          style={fieldStyles}
          inputStyles={{ color: theme.palette.foreground.default }}
          endAdornment={<Icon name="calendar" color={theme.palette.foreground.dimmed} />}
          value={value ?? undefined}
          displayFormat={"DD.MM.yyyy"}
          onChange={val => onChange(val ?? null)}
          error={!!error}
          placeholder="-"
          autoFocus={autoFocus}
        />
      );
    }

    return (
      <Input
        size="small"
        type={field.type === "number" ? "number" : "text"}
        variant="blended"
        style={fieldStyles}
        inputStyles={{
          color: theme.palette.foreground.default
        }}
        value={value ?? ""}
        onChange={val => onChange(val)}
        error={!!error}
        placeholder="-"
        autoFocus={autoFocus}
      />
    );
  };

  return (
    <div style={{ flex: 3 }}>
      <Stack>
        <div style={{ flex: 1 }}>{resolveField()}</div>
        {onClear && <IconButton icon="crossSmall" onClick={onClear} dimmed />}
      </Stack>
      {error && (
        <Title
          color={theme.palette.danger.default}
          style={{ marginTop: theme.spacing.small, fontSize: 12 }}
        >
          {error}
        </Title>
      )}
    </div>
  );
};

export const DatasetLinkField = ({ field, value, onChange }: DatasetEntryFieldProps) => {
  const { theme } = useTheme();
  // datasets.data.list caps limit at 200; larger referenced datasets truncate in this picker
  const { entries } = useDataset(field.ref!, { offset: 0, limit: 200 });
  return (
    <Select
      size="small"
      variant="blended"
      searchable
      placeholder="-"
      style={{ width: "100%" }}
      options={(entries ?? []).map(e => ({ key: e.id, title: e.title }))}
      value={value ?? undefined}
      onSelect={opt => onChange(opt.key)}
      endAdornment={<Icon name="link" color={theme.palette.foreground.dimmed} />}
      actions={[{ key: "clear", title: "Clear", onClick: () => onChange(null) }]}
    />
  );
};

export const DatasetEmployeeField = ({ value, onChange }: DatasetEntryFieldProps) => {
  const { theme } = useTheme();
  const { employees } = useEmployees({});
  return (
    <Select
      size="small"
      variant="blended"
      searchable
      placeholder="-"
      style={{ width: "100%" }}
      options={(employees ?? []).map(e => ({ key: e.id ?? "", title: e.name! }))}
      value={value ?? undefined}
      onSelect={opt => onChange(opt.key)}
      endAdornment={<Icon name="person" color={theme.palette.foreground.dimmed} />}
      actions={[{ key: "clear", title: "Clear", onClick: () => onChange(null) }]}
    />
  );
};
